import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Dialog from '../UI/Dialog';

import axios from '../../utils/axios';
import { useLoading } from '../../context/loading-context';
import { useErrorHandler } from '../../context/error-handler-context';

import styles from './ConnectionFinancialStatements.module.scss';
import { RobotImage } from '../UI/RobotImage';
import { FinancialStatementType, PartyType } from '../../types/api';
import { useAppSelector } from '../../selectors/selectors';
import Button from '../UI/Button';
import FileUpload from '../Common/FileUpload';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';

const PERIOD_LENGTH = 3;

type FinancialStatementTypeOption = {
  key: FinancialStatementType;
  name: string;
  partyTypes: PartyType[];
  countries: string[];
};

interface IConnectionFinancialStatementsProps {
  onClose: (value: boolean) => void;
  open: boolean;
  customerId: string;
}

const ConnectionFinancialStatements: FC<
  IConnectionFinancialStatementsProps
> = ({ customerId, onClose, open }) => {
  const { t } = useTranslation();

  const { setLoading } = useLoading();
  const { setError } = useErrorHandler();

  const [financialStatementTypes, setFinancialStatementTypes] = useState<
    FinancialStatementTypeOption[]
  >([]);

  const [statementType, setStatementType] =
    useState<FinancialStatementType | null>(null);

  const [period, setPeriod] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);

  const {
    currentCustomer: { currentCustomer },
  } = useAppSelector((state) => state);

  useEffect(() => {
    axios
      .get(`customers/financial-statement-types`)
      .then((res) => {
        setFinancialStatementTypes(res.data);
      })
      .catch((err) => {
        setError(err);
      });
  }, []);

  const handleClose = (success = false) => {
    resetDialog();
    onClose(success);
  };

  const resetDialog = () => {
    setStatementType(null);
    setPeriod(null);
    setFile(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!statementType || !period || !file) {
      return;
    }

    handleSendFinancialStatement({
      type: statementType,
      period,
      file,
    });
  };

  const handleSendFinancialStatement = (data: {
    type: FinancialStatementType;
    period: string;
    file: File;
  }) => {
    setLoading(true);

    const formData = new FormData();
    formData.append('type', data.type);
    formData.append('period', data.period);
    formData.append('file', data.file);

    axios
      .post(`customers/${customerId}/financial-statements`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((res) => {
        setLoading(false);
        handleClose(true);
      })
      .catch((err) => {
        setLoading(false);
        setError(err);
      });
  };

  const currentYear = new Date().getFullYear();
  const periodOptions = Array.from(
    { length: PERIOD_LENGTH },
    (_, i) => currentYear - PERIOD_LENGTH + i + 1
  ).map((year) => ({
    value: `${year}${year === currentYear ? '-preliminary' : ''}`,
    label: `${year}${year === currentYear ? ` - ${t('connections-financial-statements.preliminary-figures')}` : ''}`,
  }));

  const currentPartyType = currentCustomer?.fop
    ? PartyType.PERSON
    : PartyType.ORGANIZATION;
  const customerCountry = currentCustomer?.country || '';

  const filteredStatementTypeOptions = financialStatementTypes.filter(
    (statement) =>
      statement.partyTypes.includes(currentPartyType) &&
      statement.countries.includes(customerCountry)
  );

  return (
    <Dialog
      open={open}
      title={t('connections-financial-statements.dialog-title')}
      handleClose={handleClose}
    >
      <form onSubmit={handleSubmit} className={styles['dialog-wrapper']}>
        <RobotImage />
        <div className={styles['uploader-wrapper']}>
          <div className={styles['form-block']}>
            <p>{t('connections-financial-statements.dialog-description')}</p>
            <FileUpload
              allowedFileTypes={['application/pdf']}
              onFileSelect={(selectedFile) => setFile(selectedFile)}
            />

            <FormControl fullWidth>
              <InputLabel id='period-select-label' required>
                {t('connections-financial-statements.period')}
              </InputLabel>
              <Select
                labelId='period-select-label'
                id='period-select'
                label={t('connections-financial-statements.period')}
                value={period ?? ''}
                onChange={(event: SelectChangeEvent<string>) => {
                  setPeriod(event.target.value);
                }}
                required
                fullWidth
              >
                {periodOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id='statement-type-select-label' required>
                {t('connections-financial-statements.document-type')}
              </InputLabel>
              <Select
                labelId='statement-type-select-label'
                id='statement-type-select'
                label={t('connections-financial-statements.document-type')}
                value={statementType ?? ''}
                onChange={(event: SelectChangeEvent<string>) => {
                  setStatementType(
                    event.target.value as FinancialStatementType
                  );
                }}
                required
                fullWidth
              >
                {filteredStatementTypeOptions.map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {t(`connections-financial-statements.type.${option.key}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <Box className={styles['button-wrapper']}>
            <Button
              type='button'
              className='secondary'
              onClick={() => handleClose(false)}
            >
              {t('connections-financial-statements.cancel')}
            </Button>

            <Button type='submit' className='application'>
              {t('connections-financial-statements.upload')}
            </Button>
          </Box>
        </div>
      </form>
    </Dialog>
  );
};

export default ConnectionFinancialStatements;
